import React, { useMemo, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";

const formatSenderDisplayLabel = number => {
  if (!number) return "";
  const phone = String(
    number.whatsAppBusinessNumber ||
      number.displayPhoneNumber ||
      number.phoneNumber ||
      number.phoneNumberId ||
      ""
  ).trim();
  const name = String(
    number.senderDisplayName || number.displayName || number.name || ""
  ).trim();

  if (phone && name) return `${phone} (${name})`;
  return phone || name || "";
};

export default function AutoReplyTestConsole() {
  const { business } = useAuth() || {};
  const [incomingText, setIncomingText] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const effectiveBusinessId = useMemo(() => {
    const fromAuth = business?.id;
    if (typeof fromAuth === "string" && fromAuth.trim()) return fromAuth.trim();

    try {
      const fromStorage = localStorage.getItem("sa_selectedBusinessId");
      return typeof fromStorage === "string" && fromStorage.trim()
        ? fromStorage.trim()
        : null;
    } catch {
      return null;
    }
  }, [business?.id]);

  const missingBusinessMessage = "Please select a business first (SuperAdmin).";

  React.useEffect(() => {
    let cancelled = false;
    axiosClient.get("/whatsappsettings/META_CLOUD/numbers")
      .then(({ data }) => {
        if (!cancelled) setPhoneNumbers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPhoneNumbers([]);
      });
    return () => { cancelled = true; };
  }, [effectiveBusinessId]);

  const runTest = async () => {
    if (!effectiveBusinessId) {
      toast.error(missingBusinessMessage);
      return;
    }

    if (!incomingText.trim() || !phoneNumberId) return;

    setLoading(true);
    setResult(null);
    try {
      const { data } = await axiosClient.post("autoreplyflows/test-match", {
        businessId: effectiveBusinessId,
        phoneNumberId,
        incomingText: incomingText.trim(),
      });
      setResult(data || null);
    } catch (err) {
      console.error("AutoReply test-match failed", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to run auto-reply test."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-[#f5f6f7] min-h-[calc(100vh-80px)]">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
        <h1 className="text-lg font-semibold text-slate-900">
          AutoReply Test Console
        </h1>

        {!effectiveBusinessId && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {missingBusinessMessage}
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            WhatsApp number
          </label>
          <select
            value={phoneNumberId}
            onChange={e => setPhoneNumberId(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Select WhatsApp number</option>
            {phoneNumbers.map(number => (
              <option key={number.phoneNumberId} value={number.phoneNumberId}>
                {formatSenderDisplayLabel(number)}
              </option>
            ))}
          </select>
          <label className="block text-sm font-medium text-slate-700">
            Incoming text
          </label>
          <textarea
            rows={4}
            value={incomingText}
            onChange={e => setIncomingText(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="Type incoming message text to test matching..."
          />
          <button
            type="button"
            onClick={runTest}
            disabled={loading || !incomingText.trim() || !phoneNumberId}
            className="inline-flex items-center gap-1.5 h-9 rounded-md border border-emerald-600 bg-emerald-600 px-3 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Testing..." : "Run Test"}
          </button>
        </div>

        {result && (
          <div className="border border-slate-200 rounded-md p-3 text-sm">
            {result.isMatch ? (
              <div className="space-y-1 text-slate-700">
                <div>
                  <span className="font-medium">Flow Name:</span>{" "}
                  {result.flowName || "-"}
                </div>
                <div>
                  <span className="font-medium">Matched Keyword:</span>{" "}
                  {result.matchedKeyword || "-"}
                </div>
              </div>
            ) : (
              <div className="text-slate-600">
                No matching flow found for this input.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

